import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { LysepAPI } from '../../contexts/ApiContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { PERMISSIONS } from '../../constants/permissions';
import AddUserModal from './components/AddUserModal';
import NotificationSender from './components/NotificationSender';
import UserList from './components/UserList';
import UserDevicesModal from './components/UserDevicesModal';
import { UserPlus, Users } from 'lucide-react';

const UsersModule = () => {
  const { hasPermission, getAllRoles, assignRoleToUser } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Modal States
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [selectedUserForDevices, setSelectedUserForDevices] = useState(null);
  const [isDevicesOpen, setIsDevicesOpen] = useState(false);

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  const fetchRoles = async () => {
    try {
      if (getAllRoles) {
        const fetchedRoles = await getAllRoles();
        setRoles(fetchedRoles || []);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Roller yüklenirken hata oluştu');
    } finally {
      setLoadingRoles(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await LysepAPI.get('/api/user');
      if (response.data.state && response.data.data) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Users fetch error:', error);
      toast.error('Kullanıcı listesi alınamadı');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserAdded = useCallback(() => {
    fetchUsers();
  }, []);

  const handleUpdateUser = async (userId, updates) => {
    if (updates.role) {
      // Handle Role Update
      try {
        if (assignRoleToUser) {
          const result = await assignRoleToUser(userId, updates.role);
          if (result?.success) {
            toast.success('Rol güncellendi');
            // Optimistic update or refetch
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: updates.role } : u));
          } else {
            toast.error(result?.message || 'Rol güncellenemedi');
          }
        }
      } catch (error) {
        toast.error(`Hata: ${error.message || 'Bilinmeyen hata'}`);
      }
    }

    if (updates.isActive !== undefined) {
      // Handle Active Status Update
      try {
        const response = await LysepAPI.put('/api/user', {
          id: userId,
          isActive: updates.isActive
        });

        if (response.data?.state) {
          toast.success(response.data.message || 'Kullanıcı durumu güncellendi');
          // Optimistic update
          setUsers(prev => prev.map(u => u.id === userId ? { ...u, isActive: updates.isActive } : u));
        } else {
          toast.error(response.data?.message || 'Durum güncellenemedi');
        }
      } catch (error) {
        console.error('Update isActive error:', error);
        const errorMessage = error.response?.data?.message || 'Durum güncellenirken hata oluştu';
        toast.error(errorMessage);
      }
    }
  };

  const canCreate = hasPermission ? hasPermission(PERMISSIONS.USER_CREATE) : false;

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Users className="w-8 h-8 text-orange-500" />
            Kullanıcı Yönetimi
          </h1>
          <p className="text-gray-400 mt-1">Sistem kullanıcılarını ve rollerini yönetin</p>
        </div>

        {canCreate && (
          <button
            onClick={() => setIsAddUserOpen(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium shadow-lg shadow-orange-500/20"
          >
            <UserPlus className="w-4 h-4" />
            Yeni Kullanıcı Ekle
          </button>
        )}
      </div>

      {/* Notification Area */}
      <NotificationSender users={users} />

      {/* Users List */}
      <UserList
        users={users}
        roles={roles}
        onUpdateUser={handleUpdateUser}
        onOpenDevices={(user) => {
          setSelectedUserForDevices(user);
          setIsDevicesOpen(true);
        }}
      />

      {/* Modals */}
      <AddUserModal
        isOpen={isAddUserOpen}
        onClose={() => setIsAddUserOpen(false)}
        roles={roles}
        onSuccess={handleUserAdded}
      />

      <UserDevicesModal
        isOpen={isDevicesOpen}
        onClose={() => {
          setIsDevicesOpen(false);
          setSelectedUserForDevices(null);
        }}
        user={selectedUserForDevices}
      />
    </div>
  );
};

export default UsersModule;
